import React from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

import {
  bindSingleAssetAgentView,
  createSingleAssetAgentConversation,
  getSingleAssetAgentConversation,
  getSingleAssetMarketView,
  getAssetTrackingMarketPulse,
  getAssetTrackingOverview,
  getMarketIndexCards,
  getMarketInstrumentLookup,
  removeAssetWatch,
} from "../../api/client";
import styles from "./assetOverview.module.css";
import { AssetTagEditor } from "./AssetTagEditor";
import { TagFilterMenu } from "./TagFilterMenu";
import {
  filterByTags,
  holdingTotalCost,
  summarizeHoldings,
  tagCounts,
} from "./holdingSummaryModel";
import { selectablePickerInstrument } from "./assetPickerModel";
import { openHoldingPage } from "./openHolding";
import { historyMessage } from "../single-asset-agent-dev/agentMessageFormat";
import { useOverviewAgent } from "./OverviewAgentPanel";

function signed(value) {
  if (missing(value)) return "--";
  const number = Number(value);
  if (!Number.isFinite(number)) return "--";
  return `${number > 0 ? "+" : ""}${number.toFixed(2)}%`;
}

// Number(null) 和 Number("") 都是 0，直接丢给 Number.isFinite 会把"算不出来"
// 渲染成 0.00%。只有基线、还没有第二个交易日的持仓正好走这条路，用户会把它
// 读成"这只票没赚没亏"（§9.3.2：不可用只能显示 --）。
function missing(value) {
  return value === null || value === undefined || value === "";
}

// 指数点位：后端把「实时源不可用且收盘价过期」的卡片降级成 status=unavailable（没有 close），
// 这里必须显示 --，否则 Number(undefined) 会渲染成 NaN。
function indexLevel(value) {
  if (missing(value) || !Number.isFinite(Number(value))) return "--";
  return Number(value).toLocaleString("zh-CN", { maximumFractionDigits: 2 });
}

function number(value, digits = 2) {
  if (missing(value)) return "--";
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("zh-CN", { maximumFractionDigits: digits, minimumFractionDigits: digits })
    : "--";
}

function pct(value) {
  if (missing(value)) return "--";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${parsed > 0 ? "+" : ""}${(parsed * 100).toFixed(2)}%` : "--";
}

function amountYi(value) {
  if (missing(value)) return "--";
  const parsed = Number(value);
  // stock_daily.amount is quoted in thousand yuan.
  return Number.isFinite(parsed) ? `${(parsed / 100000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 亿` : "--";
}

function marketCapYi(value) {
  if (missing(value)) return "--";
  const parsed = Number(value);
  // daily_basic.total_mv is quoted in ten-thousand yuan.
  return Number.isFinite(parsed) ? `${(parsed / 10000).toLocaleString("zh-CN", { maximumFractionDigits: 2 })} 亿` : "--";
}

function tone(value) {
  if (missing(value)) return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed < 0 ? styles.down : styles.up;
}

function SortButton({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const direction = active ? (sort.direction === "asc" ? "升序" : "降序") : "未排序";
  return <button type="button" className={styles.sortButton} onClick={() => onSort(sortKey)} aria-label={`按${label}排序，当前${direction}`}>{label} <span aria-hidden="true">{active && sort.direction === "asc" ? "↑" : "↓"}</span></button>;
}

function SortHeader(props) {
  return <th><SortButton {...props} /></th>;
}

// 「价格 | 涨跌」「总盈亏 | %」两格里的两个数各自可排序（AC-105）。
function SplitSortHeader({ first, second, sort, onSort }) {
  return (
    <th>
      <span className={styles.splitHead}>
        <SortButton label={first[0]} sortKey={first[1]} sort={sort} onSort={onSort} />
        <i>|</i>
        <SortButton label={second[0]} sortKey={second[1]} sort={sort} onSort={onSort} />
      </span>
    </th>
  );
}

// 持仓汇总：复用「主要指数」的小卡片，顺序跟表格列走（AC-102）。
function HoldingSummary({ summary, scope }) {
  const cards = [
    ["今日盈亏", summary.today_pnl, summary.today_pct, true],
    ["总成本", summary.total_cost],
    ["持仓市值", summary.market_value],
    ["分红", summary.dividend_income],
    ["已实现收益", summary.realized_pnl, undefined, true],
    ["持仓收益", summary.unrealized_pnl, undefined, true],
    ["总盈亏", summary.total_profit, summary.book_total_return, true],
  ];
  return (
    <>
      <div className={styles.sumHead}>持仓汇总<span>{scope}</span></div>
      <div className={`${styles.indexGrid} ${styles.sumGrid}`}>
        {cards.map(([label, value, ratio, colored]) => (
          <article key={label} className={styles.indexCard}>
            <span>{label}</span>
            <strong className={colored ? tone(value) : ""}>{number(value)}</strong>
            {ratio !== undefined && <small className={tone(ratio)}>{pct(ratio)}</small>}
          </article>
        ))}
      </div>
    </>
  );
}

function AssetIdentityLink({ item, onOpen }) {
  const nameRef = React.useRef(null);
  const assetName = item.asset_name || item.asset_code;

  React.useLayoutEffect(() => {
    const element = nameRef.current;
    if (!element) return undefined;
    const fit = () => {
      // Measure once at the normal size. Long names get one bounded reduction;
      // ellipsis remains the final guard instead of shrinking into illegibility.
      element.dataset.compact = "false";
      element.dataset.compact = element.scrollWidth - element.clientWidth > 1 ? "true" : "false";
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [assetName]);

  return (
    <button className={styles.assetLink} title={assetName} onClick={onOpen}>
      <span ref={nameRef} className={styles.assetName} data-compact="false">{assetName}</span>
      <small>{item.asset_code}</small>
    </button>
  );
}

function sortRows(rows, sort, valueFor) {
  if (!sort.key) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const a = Number(valueFor(left, sort.key));
    const b = Number(valueFor(right, sort.key));
    if (!Number.isFinite(a) && !Number.isFinite(b)) return 0;
    if (!Number.isFinite(a)) return 1;
    if (!Number.isFinite(b)) return -1;
    return (a - b) * direction;
  });
}

function CollapsibleSection({ title, count, entry, children }) {
  const [open, setOpen] = React.useState(true);
  return (
    <section className={styles.section}>
      <header className={styles.sectionHead}>
        <div className={styles.sectionTitle}><strong>{title}</strong>{count !== "" && <span>{count}</span>}</div>
        {entry}
        <div className={styles.sectionActions}>
          <button
            type="button"
            className={styles.sectionToggle}
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-label={`${open ? "收起" : "展开"}${title}`}
            title={`${open ? "收起" : "展开"}${title}`}
          >
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </header>
      {open && children}
    </section>
  );
}

const WATCH_TYPE_LABELS = { stock: "A股", etf: "ETF", open_fund: "场外基金" };
// 「添加关注」已改成独立页 /screener（自然语言查找，features/screener）；这个弹窗只剩「新增持仓」用。

function isCnContinuousTradingSession(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now).map((part) => [part.type, part.value]));
  if (["Sat", "Sun"].includes(parts.weekday)) return false;
  const minute = Number(parts.hour) * 60 + Number(parts.minute);
  return (minute >= 570 && minute <= 690) || (minute >= 780 && minute <= 900);
}

// 新增持仓的标的选择：已有 Holding 直接打开核算；未持有则打开行情并自动弹出「记录交易」。
// 持仓只能在结构化录入通过后转正。
function WatchPicker({ onClose, onOpenAsset, onOpenExistingHolding }) {
  const title = "新增持仓";
  const [input, setInput] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const query = input.trim();
    if (!query) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const payload = await getMarketInstrumentLookup({ query, limit: 20 });
        if (active) setResults(payload?.data?.items ?? []);
      } catch {
        // 搜索不可用时保持空结果并拒绝提交；不能把未经确认的输入冒充 asset_code。
        if (active) setResults([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [input]);

  async function add(asset) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (await onOpenExistingHolding(asset)) {
        onClose();
        return;
      }
      await onOpenAsset(asset);
      onClose();
    } catch (requestError) {
      setError(requestError.message || "暂时无法打开该标的。");
    } finally {
      setBusy(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    const instrument = selectablePickerInstrument(results, input);
    if (!instrument) {
      setError(searching
        ? "正在确认标的，请稍后再试。"
        : "请从搜索结果中选择唯一标的。");
      return;
    }
    void add(instrument.code);
  }

  const query = input.trim();
  return (
    <div className={styles.modalBackdrop} role="presentation">
      <form className={styles.modal} onSubmit={submit} aria-label={title}>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="关闭"><X size={18} /></button>
        <h2>{title}</h2>
        <p>搜索全部标的（A股、ETF、场外基金），选中后在核算页录入持仓。</p>
        <input autoFocus value={input} onChange={(event) => {
          const value = event.target.value;
          setInput(value);
          setResults([]);
          setSearching(Boolean(value.trim()));
          setError("");
        }} placeholder="例如：贵州茅台 / 600519" aria-label="搜索标的" />
        {query && (
          <div className={styles.pickerResults} role="listbox" aria-label="搜索结果">
            {results.map((item) => (
              <button
                type="button"
                role="option"
                aria-selected="false"
                key={item.instrument_id}
                className={styles.pickerResult}
                disabled={busy}
                onClick={() => void add(item.code)}
              >
                <strong>{item.name}</strong>
                <small>{item.code}</small>
                <em>{WATCH_TYPE_LABELS[item.instrument_type] ?? item.instrument_type}</em>
              </button>
            ))}
            {!results.length && (
              <div className={styles.pickerEmpty}>{searching ? "搜索中…" : "没有匹配的标的"}</div>
            )}
          </div>
        )}
        {error && <small className={styles.modalError}>{error}</small>}
        <button className={styles.primary} disabled={!query || busy || searching} type="submit">{busy ? "正在打开…" : "录入持仓"}</button>
      </form>
    </div>
  );
}

export function AssetOverview({ navigate, onLogout, initialAgentHandoff = null }) {
  const [overview, setOverview] = React.useState({ holdings: [], watches: [] });
  const [indices, setIndices] = React.useState(null);  // null = 还在取，[] = 取不到
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [pickerMode, setPickerMode] = React.useState("");
  const [holdingSort, setHoldingSort] = React.useState({ key: null, direction: "desc" });
  const [watchSort, setWatchSort] = React.useState({ key: null, direction: "desc" });
  const [tagFilter, setTagFilter] = React.useState([]);
  const agent = useOverviewAgent({ navigate, onLogout, initialAgentHandoff, onError: setError });
  const agentHandoff = agent.agentHandoff;

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await getAssetTrackingOverview());
      setError("");
    } catch (requestError) {
      setError(requestError.message || "概览暂时无法加载。");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const refreshMarket = React.useCallback(async () => {
    const [pulseResult, indexResult] = await Promise.allSettled([
      getAssetTrackingMarketPulse(),
      getMarketIndexCards(),
    ]);
    if (pulseResult.status === "fulfilled") {
      const patches = new Map((pulseResult.value.watches ?? []).map((item) => [item.asset_code, item.market]));
      setOverview((current) => ({
        ...current,
        watches: (current.watches ?? []).map((item) => (
          patches.has(item.asset_code) ? { ...item, market: patches.get(item.asset_code) } : item
        )),
      }));
    }
    if (indexResult.status === "fulfilled") {
      const market = indexResult.value;
      setIndices([...(market?.domestic_indices ?? []), ...(market?.global_indices ?? [])].slice(0, 12));
    } else {
      setIndices((current) => current ?? []);
    }
  }, []);

  // 首次进入和切回页面立即刷新；只有 A 股连续交易时段保持 30 秒轮询。
  // 页面隐藏、离开组件或收盘后都没有常驻定时请求。
  React.useEffect(() => {
    let timer = null;
    const sync = ({ immediate = false } = {}) => {
      if (timer) window.clearTimeout(timer);
      timer = null;
      if (document.hidden) return;
      if (immediate || isCnContinuousTradingSession()) void refreshMarket();
      // Outside the session this only re-evaluates the clock; it sends no request.
      timer = window.setTimeout(sync, 30_000);
    };
    const handleVisibility = () => sync({ immediate: true });
    document.addEventListener("visibilitychange", handleVisibility);
    sync({ immediate: true });
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timer) window.clearTimeout(timer);
    };
  }, [refreshMarket]);

  const replaceRowTags = React.useCallback((assetCode, tags) => {
    setOverview((current) => ({
      holdings: (current.holdings ?? []).map((item) => item.asset_code === assetCode ? { ...item, tags } : item),
      watches: (current.watches ?? []).map((item) => item.asset_code === assetCode ? { ...item, tags } : item),
    }));
  }, []);
  const allHoldings = overview.holdings ?? [];
  const allWatches = overview.watches ?? [];
  // 标签筛选同时作用于持仓表、关注表和持仓汇总（AC-103）。
  const tagOptions = tagCounts([...allHoldings, ...allWatches]);
  const filtering = tagFilter.length > 0;
  const holdings = sortRows(filterByTags(allHoldings, tagFilter), holdingSort, (item, key) => {
    const metrics = item.metrics ?? {};
    if (key === "today_profit") return missing(metrics.quantity) || missing(metrics.today_change)
      ? null : Number(metrics.quantity) * Number(metrics.today_change);
    // 排序取的值必须和这一格显示的值同源，否则点了没反应或者排出来对不上。
    if (key === "total_cost") return holdingTotalCost(metrics);
    if (key === "return_pct") return metrics.book_total_return ?? metrics.total_return;
    return metrics[key];
  });
  const watches = sortRows(filterByTags(allWatches, tagFilter), watchSort, (item, key) => (item.market ?? {})[key]);
  const summary = summarizeHoldings(holdings);
  const openTagSummary = (label) => navigate(`/tag/${encodeURIComponent(label)}`);
  const tagFilterMenu = <TagFilterMenu tags={tagOptions.map(([label]) => label)} counts={Object.fromEntries(tagOptions)} selected={tagFilter} onChange={setTagFilter} />;
  const toggleHoldingSort = (key) => setHoldingSort((current) => ({ key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc" }));
  const toggleWatchSort = (key) => setWatchSort((current) => ({ key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc" }));

  async function openHolding(item) {
    setError("");
    try {
      await openHoldingPage(navigate, item, { agentHandoff });
    } catch (requestError) {
      setError(requestError.message || "暂时无法打开该持仓。");
    }
  }

  // `openHoldingEntry` 从「新增持仓」那条路进来时为真：用户的意图是**录入**，
  // 不是看行情，所以落地后要直接把录入弹窗打开。只跳转到行情页等于把他撂在
  // 半路上——他还得自己找到「记录交易」再点一次。
  async function openWatch(item, { openHoldingEntry = false } = {}) {
    setError("");
    try {
      const knownConversationId = agentHandoff?.turnResult?.conversation_id
        || window.sessionStorage.getItem("single_asset_agent_active_conversation");
      // 路由交接只负责让页面先显示；revision 必须从服务端会话读取。
      // 内嵌 Agent 的结构化录入回执并不是完整会话，直接拿它绑定会漏掉 revision。
      const latest = knownConversationId
        ? await getSingleAssetAgentConversation(knownConversationId, { historyLimit: 200 })
        : await createSingleAssetAgentConversation();
      const binding = await bindSingleAssetAgentView(
        latest.conversation_id,
        latest.revision,
        { type: "market", asset_code: item.asset_code },
      );
      const handedOffMessages = (latest.history ?? []).map(historyMessage);
      navigate(`/single?conversation=${encodeURIComponent(latest.conversation_id)}&binding=${encodeURIComponent(binding.binding_id)}&asset=${encodeURIComponent(item.asset_code)}`, {
        state: {
          singleAssetAgentHandoff: {
            turnResult: {
              ...latest,
              revision: binding.conversation_revision,
              current_binding_id: binding.binding_id,
            },
            messages: handedOffMessages,
          },
          // 行情体积远大于 binding。先完成路由，让录入弹窗立即出现；工作台在挂载后
          // 异步加载行情，不能让 60d/全历史传输阻塞“点击结果 → 进入页面”。
          singleAssetBindingView: { binding },
          singleAssetOpenHoldingEntry: openHoldingEntry,
        },
      });
    } catch (requestError) {
      setError(requestError.message || "暂时无法打开该标的行情。");
    }
  }

  return (
    <main className={styles.page} id="main-content" style={agent.style}>
      {agent.panel}
      <div className={styles.content}>
        <CollapsibleSection title="主要指数" count="" >
          <div className={styles.indexGrid}>
            {indices === null ? <div className={styles.empty}>正在获取主要指数…</div> : indices.length ? indices.map((item) => <article key={item.code} className={styles.indexCard}><span>{item.name}</span><strong>{indexLevel(item.close)}</strong><small className={Number(item.pct_chg) < 0 ? styles.down : styles.up}>{signed(item.pct_chg)}</small></article>) : <div className={styles.empty}>主要指数暂时不可用。</div>}
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          title="持仓明细"
          count={filtering ? `${holdings.length} / ${allHoldings.length}` : ""}
          entry={<button type="button" className={styles.selectButton} onClick={() => setPickerMode("holding")}>新增持仓</button>}
        >
          {holdings.length > 0 && (
            <HoldingSummary
              summary={summary}
              scope={filtering ? `「${tagFilter.join("、")}」${holdings.length} 只` : `全部 ${holdings.length} 只`}
            />
          )}
          <div className={`${styles.tableWrap} ${styles.holdingTable}`}>
            <table>
              <thead>
                <tr>
                  <th>标的</th>
                  <th><span className={styles.tagHead}>标签{tagFilterMenu}</span></th>
                  <SplitSortHeader first={["价格", "latest_price"]} second={["涨跌", "today_pct_change"]} sort={holdingSort} onSort={toggleHoldingSort} />
                  <SortHeader label="持股数" sortKey="quantity" sort={holdingSort} onSort={toggleHoldingSort} />
                  <SortHeader label="总成本" sortKey="total_cost" sort={holdingSort} onSort={toggleHoldingSort} />
                  <SortHeader label="持仓市值" sortKey="market_value" sort={holdingSort} onSort={toggleHoldingSort} />
                  <SortHeader label="分红" sortKey="dividend_income" sort={holdingSort} onSort={toggleHoldingSort} />
                  <SortHeader label="已实现收益" sortKey="realized_pnl" sort={holdingSort} onSort={toggleHoldingSort} />
                  <SplitSortHeader first={["总盈亏", "total_profit"]} second={["%", "return_pct"]} sort={holdingSort} onSort={toggleHoldingSort} />
                </tr>
              </thead>
              <tbody>
                {holdings.length ? holdings.map((item) => {
                  const metrics = item.metrics ?? {};
                  return (
                    <tr key={item.asset_code}>
                      <td><AssetIdentityLink item={item} onOpen={() => { void openHolding(item); }} /></td>
                      <td className={styles.tagCell}><AssetTagEditor assetCode={item.asset_code} initialTags={item.tags} compact onTagClick={openTagSummary} onChanged={(tags) => replaceRowTags(item.asset_code, tags)} /></td>
                      <td className={styles.priceCell}><b>{number(metrics.latest_price)}</b><small className={tone(metrics.today_pct_change)}>{signed(metrics.today_pct_change)}</small></td>
                      <td>{number(metrics.quantity, 0)}</td>
                      <td>{number(holdingTotalCost(metrics))}</td>
                      <td>{number(metrics.market_value)}</td>
                      <td>{number(metrics.dividend_income)}</td>
                      <td className={tone(metrics.realized_pnl)}>{number(metrics.realized_pnl)}</td>
                      <td className={tone(metrics.total_profit)}><b>{number(metrics.total_profit)}</b><small>{pct(metrics.book_total_return ?? metrics.total_return)}</small></td>
                    </tr>
                  );
                }) : <tr><td colSpan="9" className={styles.empty}>{filtering ? "没有带所选标签的持仓。" : "暂无持有标的。"}</td></tr>}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          title="关注列表"
          count={filtering ? `${watches.length} / ${allWatches.length}` : ""}
          entry={<button type="button" className={styles.selectButton} onClick={() => navigate("/screener")}>添加关注</button>}
        >
          <div className={`${styles.tableWrap} ${styles.watchTable}`}>
            <table>
              <thead>
                <tr>
                  <th>标的</th>
                  <th><span className={styles.tagHead}>标签{tagFilterMenu}</span></th>
                  <SplitSortHeader first={["价格", "latest_price"]} second={["涨跌", "today_change"]} sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="开盘" sortKey="open" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="最高" sortKey="high" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="最低" sortKey="low" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="成交额" sortKey="amount" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="流通市值" sortKey="circ_mv" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="PE" sortKey="pe" sort={watchSort} onSort={toggleWatchSort} />
                  <SortHeader label="PB" sortKey="pb" sort={watchSort} onSort={toggleWatchSort} />
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {watches.length ? watches.map((item) => {
                  const market = item.market ?? {};
                  return (
                    <tr key={item.asset_code}>
                      <td><AssetIdentityLink item={item} onOpen={() => { void openWatch(item); }} /></td>
                      <td className={styles.tagCell}><AssetTagEditor assetCode={item.asset_code} initialTags={item.tags} compact onTagClick={openTagSummary} onChanged={(tags) => replaceRowTags(item.asset_code, tags)} /></td>
                      <td className={styles.priceCell}><b>{number(market.latest_price)}</b><small className={tone(market.today_change)}>{pct(market.today_change)}</small></td>
                      <td>{number(market.open)}</td>
                      <td>{number(market.high)}</td>
                      <td>{number(market.low)}</td>
                      <td>{amountYi(market.amount)}</td>
                      <td>{marketCapYi(market.circ_mv)}</td>
                      <td>{number(market.pe, 2)}</td>
                      <td>{number(market.pb, 2)}</td>
                      <td><button className={styles.remove} onClick={async () => { await removeAssetWatch(item.asset_code); setOverview((current) => ({ ...current, watches: (current.watches ?? []).filter((watch) => watch.asset_code !== item.asset_code) })); }}>移除</button></td>
                    </tr>
                  );
                }) : <tr><td colSpan="11" className={styles.empty}>{filtering ? "没有带所选标签的关注。" : "暂无关注标的。"}</td></tr>}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      </div>
      {loading && <div className={styles.loading} role="status">正在加载概览…</div>}
      {error && <div className={styles.error} role="alert">{error}</div>}
      {pickerMode && (
        <WatchPicker
          onClose={() => setPickerMode("")}
          onOpenAsset={(assetCode) => openWatch({ asset_code: assetCode }, { openHoldingEntry: true })}
          onOpenExistingHolding={async (assetCode) => {
            const existing = allHoldings.find((item) => item.asset_code === assetCode);
            if (!existing) return false;
            await openHolding(existing);
            return true;
          }}
        />
      )}
    </main>
  );
}
